<template>
  <div id="app">
    <div
      v-if="player.playing"
      class="now-playing"
      :class="getNowPlayingClass()"
    >
      <div class="now-playing__container">
        <div class="now-playing__cover">
          <img 
            v-if="thumbnailUrl"
            :src="thumbnailUrl"
            :alt="player.trackTitle"
            class="now-playing__image"
            ref="albumImage"
            :href="player.songURL"
            @click="requestFullscreen"
          />
        </div>
        <div class="now-playing__details">
          <h1 class="now-playing__track" ref="trackTitle">{{ displayedTrackTitle }}</h1>
          <h2 class="now-playing__artists" ref="trackArtists">{{ displayedArtists }}</h2>
        </div>
      </div>
    </div>
    <div v-else class="now-playing" :class="getNowPlayingClass()">
      <h1 class="now-playing__idle-heading">{{ this.notPlayingMsg }}</h1>
    </div>
  </div>
</template>

<script>
// import { get } from 'core-js/core/dict';
import * as Vibrant from 'node-vibrant'

export default {
  name: 'NowPlaying',

  data() {
    return {
      pollPlaying: null,
      thumbnailUrl: null,
      thumbnailCache: new Map(),
      player: {
        playing: false,
        trackArtists: [],
        trackTitle: '',
        trackAlbum: {},
        currentTrackId: null
      },
      notPlayingMsg: this.getRandomEmoji(),
      displayedTrackTitle: '',
      displayedArtists: '',
    }
  },

  computed: {
    // getTrackArtists() {
    //   return this.player.trackArtists.join(', ')
    // }
  },

  methods: {

    requestFullscreen() {
      if (window.navigator.standalone && /iPad|iPhone|iPod|Safari/.test(navigator.userAgent)) {
        // iOS devices don't support fullscreen for elements, so we'll just open the song URL instead
        window.open(`https://open.spotify.com/track/${this.player.currentTrackId}`, '_blank')
        return
      } else if (!window.navigator.standalone && /iPad|iPhone|iPod|Safari/.test(navigator.userAgent)) {
        alert('Please add this web app to your home screen to enable fullscreen mode.')
        return
      }
      const element = this.$el; // Target the root element of the Vue component
      if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement && !document.msFullscreenElement) {
        // Enter fullscreen
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.mozRequestFullScreen) { // Firefox
          element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) { // Chrome, Safari and Opera
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) { // IE/Edge
          element.msRequestFullscreen();
        } else if (element.webkitEnterFullscreen) { // iOS Safari workaround for video elements
          element.webkitEnterFullscreen();
        } else {
            alert('Oops! Your browser doesn\'t support fullscreen. That\'s a bit sus!');
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) { // Firefox
          document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) { // Chrome, Safari and Opera
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE/Edge
          document.msExitFullscreen();
        }
      }
    },

    getRandomEmoji() {
    const emojiRanges = [
        [0x1F600, 0x1F64F], // Emoticons
      ];

      const randomRange = emojiRanges[Math.floor(Math.random() * emojiRanges.length)];
      const randomCodePoint = Math.floor(Math.random() * (randomRange[1] - randomRange[0] + 1)) + randomRange[0];
      return String.fromCodePoint(randomCodePoint);
    },
    
    getTrackIdFromUri(uri) {
      return uri.split(':').pop()
    },

    /**
     * Set the stylings of the app based on received colours.
     */
     setAppColours() {
      document.documentElement.style.setProperty(
        '--color-text-primary',
        this.colourPalette.text
      )

      document.documentElement.style.setProperty(
        '--colour-background-now-playing',
        this.colourPalette.background
      )

      this.setThemeColor(this.colourPalette.background);
    },

    /**
     * Set the theme color meta tag.
     */
    setThemeColor(color) {
      let themeColorMetaTag = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMetaTag) {
        themeColorMetaTag = document.createElement('meta');
        themeColorMetaTag.name = 'theme-color';
        document.head.appendChild(themeColorMetaTag);
      }
      themeColorMetaTag.content = color;
    },

    /**
     * Handle newly stored colour palette:
     * - Map data to readable format
     * - Get and store random colour combination.
     */
     handleAlbumPalette(palette) {
      let albumColours = Object.keys(palette)
        .filter(item => {
          return item === null ? null : item
        })
        .map(colour => {
          return {
            text: palette[colour].getTitleTextColor(),
            background: palette[colour].getHex()
          }
        })

      this.swatches = albumColours

      // this.colourPalette = albumColours[Math.floor(Math.random() * albumColours.length)]
      this.colourPalette = albumColours[albumColours.length - 1] // Get the last colour in the array

      this.$nextTick(() => {
        this.setAppColours()
      })
    },

    /**
     * Get the colour palette from the album cover.
     */
     getAlbumColours() {
      /**
       * No image; revert to default colours.
       */
      if (!this.thumbnailUrl) {
        this.colourPalette = {
          text: '#FFFFFF',
          background: '#FF3366'
        }
        this.setAppColours()
        return
      }

      /**
       * Run node-vibrant to get colours.
       */
      Vibrant.from(this.thumbnailUrl)
        .quality(1)
        .clearFilters()
        .getPalette()
        .then(palette => {
          this.handleAlbumPalette(palette)
        })
    },

    async getThumbnail(songUri) {
      const trackId = this.getTrackIdFromUri(songUri)
      if (this.player.currentTrackId !== trackId) return
      if (this.thumbnailCache.has(trackId)) {
        this.thumbnailUrl = this.thumbnailCache.get(trackId)
        this.fadeInImage(this.thumbnailCache.get(trackId), true)
        return
      }

      try {
        const response = await fetch(
          `https://open.spotify.com/oembed?url=${songUri}`
        )
        const data = await response.json()
        this.thumbnailCache.set(trackId, data.thumbnail_url)
        this.thumbnailUrl = data.thumbnail_url
        this.getAlbumColours()
        this.fadeInImage(data.thumbnail_url)
      } catch (error) {
        console.error('Error fetching thumbnail:', error)
      }
    },

    async getNowPlaying() {
      try {
        const response = await fetch(
          'https://proxy.meesum632.workers.dev/proxy?modify&proxyUrl=https://nowplaying-api.tinyrobot.co/playbackstate?key=cad84c89-2aca-4626-8f54-7a29c36b15e4',
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Origin': 'https://np.nodr.me',
              'Referer': 'https://np.nodr.me'
            },
            mode: 'cors'
          }
        )

        const data = await response.json()

				if (this.getTrackIdFromUri(data.songUri) === this.player.currentTrackId) return;
        
        if (data.isPlaying) {
          this.player = {
            playing: data.isPlaying,
            trackArtists: data.artists.map(artist => artist.name),
            trackTitle: data.songName,
            trackAlbum: {
              title: data.albumName
            },
            songURL: `https://open.spotify.com/track/${this.getTrackIdFromUri(data.songUri)}`,
            currentTrackId: this.getTrackIdFromUri(data.songUri)
          }
          await this.getThumbnail(data.songUri)
          this.fadeText(this.player.trackArtists.join(', '))
          this.matrixTextTransition(this.player.trackTitle)
          document.title = `Now Playing: ${this.player.trackTitle}`
        } else {
          this.player = {
            playing: false,
            trackArtists: [],
            trackTitle: '',
            trackAlbum: {},
            currentTrackId: null,
            songURL: ''
          }
          this.thumbnailUrl = null
          this.displayedTrackTitle = ''
          document.title = this.notPlayingMsg
          this.getAlbumColours()
        }
      } catch (error) {
        console.error('Error:', error)
        this.player.playing = false
        this.thumbnailUrl = null
        this.displayedTrackTitle = ''
        document.title = this.notPlayingMsg
        this.getAlbumColours()
      }
    },

    setDataInterval() {
      this.getNowPlaying()
      this.pollPlaying = setInterval(this.getNowPlaying, 5000)
    },

    getNowPlayingClass() {
      return {
        'now-playing': true,
        'now-playing--playing': this.player.playing
      }
    },

    fadeInImage(newURL) {
      const img = this.$refs.albumImage;
      if (img) {
      if (this.thumbnailUrl === null) {
        img.style.opacity = 0;
        this.thumbnailUrl = newURL;
        img.src = newURL;
        img.style.opacity = 1;
        return;
      }
      img.style.transition = 'opacity 2s ease-in-out, transform 2s ease-in-out';
      img.style.opacity = 0.75;
      img.style.transform = 'scale(0.97)';
      
      setTimeout(() => {
        this.thumbnailUrl = newURL;
        img.style.opacity = 1;
        img.style.transform = 'scale(1)';
      }, 750);
      }
    },

    matrixTextTransition(newText) {
      const oldText = this.displayedTrackTitle;
      let currentText = oldText.split('');

      // Remove old text from the right
      for (let i = oldText.length - 1; i >= 0; i--) {
      setTimeout(() => {
        currentText.pop();
        this.displayedTrackTitle = currentText.join('');
      }, (oldText.length - i) * 35);
      }

      // Add new text from the left after old text is completely removed
      setTimeout(() => {
      currentText = [];
      for (let i = 0; i < newText.length; i++) {
        setTimeout(() => {
        currentText[i] = newText[i];
        this.displayedTrackTitle = currentText.join('');
        }, i * 100);
      }
      }, oldText.length * 80 + 100);
    },

    // function to fade aritst name
    fadeText(newText) {
      const textElmt = this.$refs.trackArtists;
      if (textElmt) {
        textElmt.style.transition = 'opacity 1.5s ease-in-out';
        textElmt.style.opacity = 0;
        setTimeout(() => {
          this.displayedArtists = newText;
          textElmt.style.opacity = 0.85;
        }, 1750);
      }
    },
  },

  mounted() {
    this.setDataInterval()
  },

  beforeDestroy() {
    clearInterval(this.pollPlaying)
  }
}
</script>

<style src="@/styles/components/now-playing.scss" lang="scss" scoped></style>